import { Button } from '@lego/klik-ui';
import { PaperclipSolid } from '@lego/klik-ui/icons';
import { type ChangeEvent, useRef } from 'react';

type Props = {
  accept: string;
  label: string;
  onFileRead: (content: string, fileName: string) => void;
  onError: (message: string) => void;
  validate?: (file: File) => string | null;
};

export const FileUploadButton = ({ accept, label, onFileRead, onError, validate }: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const error = validate?.(file);
    if (error) {
      onError(error);
      resetInput();
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      onFileRead(content.trim(), file.name);
      resetInput();
    };
    reader.onerror = () => {
      onError(`Failed to read file "${file.name}".`);
      resetInput();
    };
    reader.readAsText(file);
  };

  return (
    <>
      <input
        accept={accept}
        onChange={handleChange}
        ref={fileInputRef}
        style={{ display: 'none' }}
        type="file"
      />
      <Button
        leftIcon={<PaperclipSolid />}
        onClick={() => fileInputRef.current?.click()}
        variant="outline"
      >
        {label}
      </Button>
    </>
  );
};
